import React, { useState, useEffect, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, message, Segmented, Statistic, Row, Col, Progress, Tooltip as AntTooltip } from 'antd';
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  UserOutlined, 
  MessageOutlined, 
  LikeOutlined,
  ReloadOutlined,
  DownloadOutlined,
  TeamOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getSessionQualityMetrics } from '../../api/api';
import type { SessionQualityMetricsResponse } from '../../types/api-models';
import { 
  type AggregationType 
} from '../../utils/dateFormatters';

const { RangePicker } = DatePicker;

interface SessionQualityMetricsProps {
  isExpanded?: boolean;
  dashboardData?: any;
}

const SessionQualityMetrics: React.FC<SessionQualityMetricsProps> = memo(({ isExpanded = false, dashboardData }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SessionQualityMetricsResponse | null>(null);
  const [aggregation, setAggregation] = useState<AggregationType>('daily');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'days'),
    dayjs()
  ]);

  useEffect(() => {
    if (!isExpanded) {
      // Collapsed view: use dashboard data (30-day snapshot)
      if (dashboardData?.session_quality) {
        setData(dashboardData.session_quality);
      }
    }
    // Expanded view: Don't auto-load, wait for user interaction
  }, [isExpanded, dashboardData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getSessionQualityMetrics(
        aggregation,
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching session quality metrics:', error);
      message.error('Failed to load session quality metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const renderCollapsedView = () => {
    if (!data) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;

    const { 
      singleTurnSessions = 0, 
      multiTurnSessions = 0, 
      sessionsWithFeedback = 0, 
      feedbackRate = 0, 
      averageSessionDepth = 0, 
      sessionDepthDistribution = [], 
      followupTypeBreakdown = { suggested_followup: 0, user_followup: 0, new_topic: 0 } 
    } = data || {};

    return (
      <div style={{ height: '100%' }}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Statistic
              title={
                <span>
                  Single Turn Sessions
                  <AntTooltip 
                    title="Sessions where users asked only one question without follow-up interactions"
                    color="var(--neutral-90)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                  </AntTooltip>
                </span>
              }
              value={singleTurnSessions}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title={
                <span>
                  Multi Turn Sessions
                  <AntTooltip 
                    title="Sessions where users engaged in multiple question-answer exchanges or follow-up questions"
                    color="var(--neutral-90)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                  </AntTooltip>
                </span>
              }
              value={multiTurnSessions}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={8}>
            <MetricCardHeader 
               title="Feedback Rate"
               value={`${Number(feedbackRate.toFixed(2))}%`}
               trend={dashboardData?.session_quality?.quality_trend}
               subtitle="Sessions with user feedback"
            />
          </Col>
        </Row>

        <div style={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={[
                  { name: 'Single Turn', value: singleTurnSessions, color: '#1890ff' },
                  { name: 'Multi Turn', value: multiTurnSessions, color: '#52c41a' }
                ]}
                cx="50%"
                cy="50%"
                innerRadius={30}
                outerRadius={60}
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                labelLine={false}
              >
                {[
                  { name: 'Single Turn', value: singleTurnSessions, color: '#1890ff' },
                  { name: 'Multi Turn', value: multiTurnSessions, color: '#52c41a' }
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value, name) => [value, name]} 
                labelFormatter={(label) => `${label} Sessions`}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderExpandedView = () => {
    return (
      <div style={{ height: '100%' }}>
        {/* Controls Row - Following Activity Trend Pattern */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
            <Segmented
              value={aggregation}
              onChange={(value) => setAggregation(value as AggregationType)}
              options={[
                { label: 'Daily', value: 'daily' },
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' },
              ]}
            />
            {aggregation === 'daily' ? (
              <>
                <span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange([dates[0], dates[1]]);
                    }
                  }}
                  format="YYYY-MM-DD"
                  style={{ width: 260 }}
                  disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
                />
              </>
            ) : (
              <span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
                Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
              </span>
            )}
          </Space>
          
          <Space>
            <Button type="primary" onClick={handleRefresh} loading={loading}>
              Apply Filters
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!data}>
              Refresh
            </Button>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={() => {
                // TODO: Implement export functionality
                message.info('Export functionality coming soon');
              }}
            >
              Export
            </Button>
          </Space>
        </Space>

        {/* Chart */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#666' }}>Loading session quality metrics...</div>
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view session quality metrics</p>
            <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            {/* Data content goes here */}
            {(() => {
              const { 
                singleTurnSessions, 
                multiTurnSessions, 
                sessionsWithFeedback, 
                feedbackRate,
                averageSessionDepth,
                sessionDepthDistribution,
                followupTypeBreakdown
              } = data;
              return (
                <>
                  {/* Summary Statistics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title={
                <span>
                  Single Turn Sessions
                  <AntTooltip 
                    title="Sessions where users asked only one question without follow-up interactions"
                    color="var(--neutral-90)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                  </AntTooltip>
                </span>
              }
              value={singleTurnSessions}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={
                <span>
                  Multi Turn Sessions
                  <AntTooltip 
                    title="Sessions where users engaged in multiple question-answer exchanges or follow-up questions"
                    color="var(--neutral-90)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                  </AntTooltip>
                </span>
              }
              value={multiTurnSessions}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="Average Session Depth"
              value={Number(averageSessionDepth.toFixed(2))}
              suffix="turns"
              prefix={<MessageOutlined />}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title={
                <span>
                  Feedback Rate
                  <AntTooltip 
                    title="Percentage of sessions where users provided feedback (thumbs up/down) on the assistant's responses"
                    color="var(--neutral-90)"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
                  </AntTooltip>
                </span>
              }
              value={Number(feedbackRate.toFixed(2))}
              suffix="%"
              prefix={<LikeOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
        </Row>

        {/* Session Type Distribution */}
        <Card title="Session Type Distribution" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sessionDepthDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="depth" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [value, 'Sessions']}
                      labelFormatter={(label) => `${label} Sessions`}
                    />
                    <Bar dataKey="count" fill="#1890ff" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ padding: '20px 0' }}>
                <h4>Session Type Breakdown</h4>
                {sessionDepthDistribution.map((item, index) => (
                  <div key={index} style={{ marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span>{item.depth}</span>
                      <span>{item.count} ({item.percentage.toFixed(1)}%)</span>
                    </div>
                    <Progress 
                      percent={item.percentage} 
                      strokeColor={index === 0 ? '#1890ff' : '#52c41a'} 
                      showInfo={false}
                    />
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Card>

        {/* Followup Type Analysis */}
        <Card title="Followup Type Analysis" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Suggested Followup', value: followupTypeBreakdown.suggested_followup, color: '#1890ff' },
                        { name: 'User Followup', value: followupTypeBreakdown.user_followup, color: '#52c41a' },
                        { name: 'New Topic', value: followupTypeBreakdown.new_topic, color: '#faad14' }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      labelLine={false}
                    >
                      {[
                        { name: 'Suggested Followup', value: followupTypeBreakdown.suggested_followup, color: '#1890ff' },
                        { name: 'User Followup', value: followupTypeBreakdown.user_followup, color: '#52c41a' },
                        { name: 'New Topic', value: followupTypeBreakdown.new_topic, color: '#faad14' }
                      ].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                      wrapperStyle={{ fontSize: 12 }}
                      iconType="circle"
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ padding: '20px 0' }}>
                <h4>Followup Type Breakdown</h4>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Suggested Followup</span>
                    <span>{followupTypeBreakdown.suggested_followup}</span>
                  </div>
                  <Progress 
                    percent={Math.round((followupTypeBreakdown.suggested_followup / (followupTypeBreakdown.suggested_followup + followupTypeBreakdown.user_followup + followupTypeBreakdown.new_topic)) * 100)} 
                    strokeColor="#1890ff" 
                    showInfo={false}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>User Followup</span>
                    <span>{followupTypeBreakdown.user_followup}</span>
                  </div>
                  <Progress 
                    percent={Math.round((followupTypeBreakdown.user_followup / (followupTypeBreakdown.suggested_followup + followupTypeBreakdown.user_followup + followupTypeBreakdown.new_topic)) * 100)} 
                    strokeColor="#52c41a" 
                    showInfo={false}
                  />
                </div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>New Topic</span>
                    <span>{followupTypeBreakdown.new_topic}</span>
                  </div>
                  <Progress 
                    percent={Math.round((followupTypeBreakdown.new_topic / (followupTypeBreakdown.suggested_followup + followupTypeBreakdown.user_followup + followupTypeBreakdown.new_topic)) * 100)} 
                    strokeColor="#faad14" 
                    showInfo={false}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Card>


        {/* Session Quality Metrics Summary */}
        {/* <Card title="Session Quality Summary">
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }}>
                  {singleTurnSessions}
                </div>
                <div style={{ color: '#666' }}>Single Turn Sessions</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  {Math.round((singleTurnSessions / (singleTurnSessions + multiTurnSessions)) * 100)}% of total
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: 24, color: '#52c41a', marginBottom: 8 }}>
                  {multiTurnSessions}
                </div>
                <div style={{ color: '#666' }}>Multi Turn Sessions</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  {Math.round((multiTurnSessions / (singleTurnSessions + multiTurnSessions)) * 100)}% of total
                </div>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ fontSize: 24, color: '#faad14', marginBottom: 8 }}>
                  {feedbackRate.toFixed(1)}%
                </div>
                <div style={{ color: '#666' }}>Feedback Rate</div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  {sessionsWithFeedback} sessions with feedback
                </div>
              </div>
            </Col>
          </Row>
        </Card> */}
                </>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

SessionQualityMetrics.displayName = 'SessionQualityMetrics';

export default SessionQualityMetrics;
